import {
	Bookmark,
	BookmarkCheck,
	CalendarDays,
	CalendarPlus,
	ChevronRight,
	CirclePlus,
	Clock,
	Heart,
	MapPin,
	MessageCircle,
	MoreHorizontal,
	Pin,
	Search,
	Share2,
	SlidersHorizontal,
	Volume2,
} from "lucide-react";
import { MockAvatar } from "@/src/common/components/app-mock/avatar";
import { MockPhoto, MockVideoThumb } from "@/src/common/components/app-mock/media";
import { MOBILE_TOKENS, MobileAppShell } from "@/src/common/components/app-mock/mobile-shell";
import {
	type EventFormat,
	getEvent,
	getPerson,
	MOCK_COMMUNITY,
	MOCK_EVENTS_LIST,
	MOCK_FEED_POSTS,
	MOCK_PEOPLE,
	MOCK_VIDEOS,
	type MockCommunityEvent,
	type MockFeedPost,
	ROLE_CHIP_LABEL,
	VIEWER,
} from "@/src/common/components/app-mock/mock-community";
import { MOCK_TOKENS, MockButton, MockSelect, MockTabs, PresenceDot } from "@/src/common/components/app-mock/primitives";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

const MOBILE_FORMAT_COLOR: Record<EventFormat, string> = {
	online: "accent",
	in_person: MOBILE_TOKENS.success,
	hybrid: MOBILE_TOKENS.warning,
};

function MobileEventCompact({ event }: { event: MockCommunityEvent }) {
	const { palette } = useCommunityVariant();
	return (
		<div className="overflow-hidden rounded-xl border" style={{ borderColor: MOBILE_TOKENS.border }} data-mock="event-card-compact">
			<MockPhoto scene={event.scene} className="h-20 w-full" rounded="rounded-none" />
			<div className="space-y-1.5 bg-white p-2.5">
				<div className="flex items-start justify-between gap-2">
					<p className="line-clamp-2 text-[11px] font-bold">{event.title}</p>
					<span
						className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold"
						style={{ background: palette.accentSoft, color: palette.accentInk }}
					>
						{event.price === 0 ? "FREE" : `$${event.price}`}
					</span>
				</div>
				<p className="flex items-center gap-1 text-[10px]" style={{ color: MOBILE_TOKENS.textSecondary }}>
					<CalendarDays className="size-3" />
					{event.dateLabel} · {event.time}
				</p>
				<p className="flex items-center gap-1 text-[10px]" style={{ color: MOBILE_TOKENS.textSecondary }}>
					{event.format === "in_person" ? <MapPin className="size-3" /> : <Clock className="size-3" />}
					{event.location}
				</p>
				<MockButton size="xs" variant="secondary" fullWidth>
					View Details
				</MockButton>
			</div>
		</div>
	);
}

function MobilePostCard({ post }: { post: MockFeedPost }) {
	const { palette } = useCommunityVariant();
	const author = getPerson(post.authorId);
	const roleLabel = ROLE_CHIP_LABEL[author.role];
	const event = post.eventId ? getEvent(post.eventId) : undefined;
	return (
		<article className="flex flex-col gap-2 py-2" data-mock="post-card" aria-hidden="true">
			{post.pinned ? (
				<p className="flex items-center gap-1 text-[10px] font-medium" style={{ color: palette.accent }}>
					<Pin className="size-3 rotate-45" fill="currentColor" />
					Pinned
				</p>
			) : null}
			<div className="flex gap-2">
				<MockAvatar person={author} size={28} />
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-1">
						<span className="truncate text-[11px] font-bold">{author.name}</span>
						{roleLabel ? (
							<span
								className="rounded-2xl px-1.5 text-[9px] capitalize"
								style={{ background: palette.accentSoftHover, color: palette.accentInk }}
							>
								{author.role === "owner" ? "Owner" : roleLabel}
							</span>
						) : null}
						<span className="text-[10px]" style={{ color: MOBILE_TOKENS.textSecondary }}>
							{post.timeAgo}
						</span>
					</div>
					{post.groups.length > 0 ? (
						<p className="truncate text-[10px]" style={{ color: MOBILE_TOKENS.textSecondary }}>
							Posted in {post.groups.join(", ")}
						</p>
					) : null}
				</div>
				<span className="flex shrink-0 items-center gap-2" style={{ color: MOBILE_TOKENS.textSecondary }}>
					{post.bookmarked ? (
						<BookmarkCheck className="size-4" fill={palette.accent} style={{ color: palette.accent }} />
					) : (
						<Bookmark className="size-4" />
					)}
					<MoreHorizontal className="size-4" />
				</span>
			</div>
			<div className="flex flex-col gap-2 pl-9">
				{event ? (
					<MobileEventCompact event={event} />
				) : (
					<>
						<p className="text-[11px] leading-snug">{post.paragraphs[0]}</p>
						{post.images ? (
							post.images.length === 1 ? (
								<MockPhoto scene={post.images[0] ?? "workshop"} className="h-32 w-full" />
							) : (
								<div className="flex gap-1">
									{post.images.map((scene) => (
										<MockPhoto key={scene} scene={scene} className="h-20 flex-1" />
									))}
								</div>
							)
						) : null}
						{post.video ? (
							<MockVideoThumb scene={post.video.scene} duration={post.video.duration} className="h-32 w-full" playSize={36} />
						) : null}
						{post.poll ? (
							<div className="space-y-1">
								{post.poll.options.map((option, index) => {
									const chosen = index === post.poll?.chosenIndex;
									return (
										<div
											key={option.label}
											className="relative overflow-hidden rounded-xl border px-3 py-1.5"
											style={{ borderColor: chosen ? palette.accent : MOBILE_TOKENS.border, borderWidth: chosen ? 2 : 1 }}
										>
											<span className="absolute inset-y-0 left-0" style={{ width: `${option.percent}%`, background: palette.accentSoft }} />
											<span className="relative flex items-center justify-between text-[10px]">
												<span className={chosen ? "font-semibold" : ""}>{option.label}</span>
												<span>{option.percent}%</span>
											</span>
										</div>
									);
								})}
								<p className="text-[10px]" style={{ color: MOBILE_TOKENS.textSecondary }}>
									{post.poll.votes} votes
								</p>
							</div>
						) : null}
					</>
				)}
				<div className="flex items-center gap-4 pt-1" style={{ color: MOBILE_TOKENS.textSecondary }}>
					<span className="inline-flex items-center gap-1 text-[10px]">
						<Heart
							className="size-4"
							fill={post.liked ? MOBILE_TOKENS.error : "none"}
							style={post.liked ? { color: MOBILE_TOKENS.error } : undefined}
						/>
						{post.likes}
					</span>
					<span className="inline-flex items-center gap-1 text-[10px]">
						<MessageCircle className="size-3.5" />
						{post.comments}
					</span>
				</div>
			</div>
		</article>
	);
}

interface MobileScreenProps {
	className?: string;
}

export function MobileFeedScreen({ className }: MobileScreenProps) {
	const { palette } = useCommunityVariant();
	const posts = MOCK_FEED_POSTS.slice(0, 3);
	return (
		<MobileAppShell activeTab="home" fab className={className}>
			<div className="px-3 pt-1.5">
				<div className="flex items-center justify-end pb-1" aria-hidden="true">
					<span
						className="inline-flex items-center gap-1 rounded-xl px-2 py-1 text-[10px] font-medium"
						style={{ background: MOCK_TOKENS.default }}
					>
						<SlidersHorizontal className="size-3" />
						Filters
						<span className="size-1.5 rounded-full" style={{ background: palette.accent }} />
					</span>
				</div>
				{posts.map((post, index) => (
					<div key={post.id}>
						{index > 0 ? <div className="mb-1 mt-1.5 h-px" style={{ background: MOBILE_TOKENS.border }} /> : null}
						<MobilePostCard post={post} />
					</div>
				))}
			</div>
		</MobileAppShell>
	);
}

export function MobileEventsScreen({ className }: MobileScreenProps) {
	const { palette } = useCommunityVariant();
	return (
		<MobileAppShell activeTab="events" fab className={className}>
			<div className="px-3 pt-2">
				<MockTabs items={["All", "Registered"]} active={0} className="w-full [&>*]:flex-1 [&>*]:text-center" />
				<ul className="mt-1 divide-y" style={{ borderColor: MOBILE_TOKENS.border }}>
					{MOCK_EVENTS_LIST.map((event) => {
						const formatColor = MOBILE_FORMAT_COLOR[event.format] === "accent" ? palette.accent : MOBILE_FORMAT_COLOR[event.format];
						return (
							<li key={event.id} className="flex items-center gap-2.5 py-2.5" data-mock="event-row" aria-hidden="true">
								<span className="relative shrink-0">
									<MockPhoto scene={event.scene} className="size-12" rounded="rounded-xl" />
									<span
										className="absolute -right-1 -top-1 rounded px-1 py-0.5 text-[8px] font-bold"
										style={
											event.price === 0
												? { background: MOBILE_TOKENS.success, color: "#fff" }
												: { background: "#fff", border: `1px solid ${MOBILE_TOKENS.border}`, color: MOBILE_TOKENS.text }
										}
									>
										{event.price === 0 ? "FREE" : `$${event.price}`}
									</span>
								</span>
								<span className="min-w-0 flex-1">
									<span className="block text-[10px]" style={{ color: MOBILE_TOKENS.textSecondary }}>
										{event.dateLabel}
									</span>
									<span className="block truncate text-[11px] font-bold">{event.title}</span>
									<span className="flex items-center gap-1 text-[10px]" style={{ color: MOBILE_TOKENS.textSecondary }}>
										<Clock className="size-3" />
										{event.time}
									</span>
									<span className="flex items-center gap-1 text-[10px] font-medium" style={{ color: formatColor }}>
										<span className="size-1.5 rounded-full" style={{ background: formatColor }} />
										{event.format === "in_person" ? "In-person" : event.format === "hybrid" ? "Hybrid" : "Online"}
									</span>
								</span>
								<ChevronRight className="size-4 shrink-0" style={{ color: MOBILE_TOKENS.textSecondary }} />
							</li>
						);
					})}
				</ul>
			</div>
		</MobileAppShell>
	);
}

export function MobileMembersScreen({ className }: MobileScreenProps) {
	return (
		<MobileAppShell activeTab="members" className={className}>
			<div className="space-y-2 px-3 pt-2" aria-hidden="true">
				<div
					className="flex h-8 items-center gap-2 rounded-xl px-2.5 text-[11px]"
					style={{ background: MOCK_TOKENS.surfaceSecondary, color: MOBILE_TOKENS.textSecondary }}
				>
					<Search className="size-3.5" />
					Search members
				</div>
				<div className="flex items-center justify-between">
					<span className="text-[11px] font-bold">Member List ({MOCK_COMMUNITY.memberCount})</span>
					<MockSelect value="All groups" />
				</div>
				<ul className="space-y-1.5">
					{MOCK_PEOPLE.filter((person) => person.id !== VIEWER.id).map((person) => (
						<li
							key={person.id}
							className="flex items-center gap-2.5 rounded-xl bg-white p-2"
							style={{ boxShadow: MOCK_TOKENS.surfaceShadow }}
							data-mock="member-card"
						>
							<span className="relative inline-flex">
								<MockAvatar person={person} size={32} />
								{person.presence === "online" ? <PresenceDot online size={10} /> : null}
							</span>
							<span className="min-w-0 flex-1">
								<span className="block truncate text-[11px] font-bold">{person.name}</span>
								<span className="block truncate text-[10px]" style={{ color: MOBILE_TOKENS.textSecondary }}>
									{person.presence === "online"
										? "Online"
										: person.presence === "recent"
											? `Active ${person.lastActive}`
											: person.groups.slice(0, 2).join(", ")}
								</span>
							</span>
							<span className="flex shrink-0 items-center gap-3">
								<CalendarPlus className="size-4" style={{ color: MOBILE_TOKENS.textSecondary }} />
								<MessageCircle className="size-4" style={{ color: MOBILE_TOKENS.text }} />
							</span>
						</li>
					))}
				</ul>
			</div>
		</MobileAppShell>
	);
}

export function MobileVideosScreen({ className }: MobileScreenProps) {
	const video = MOCK_VIDEOS[1] ?? MOCK_VIDEOS[0];
	if (!video) return null;
	const author = getPerson(video.authorId);
	return (
		<MobileAppShell activeTab="video" hideHeader dark className={className}>
			<div className="absolute inset-0" aria-hidden="true" data-mock="video-item">
				<MockPhoto scene={video.scene} className="h-full w-full" rounded="rounded-none" />
				<div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/85 to-transparent" />
				<div className="absolute inset-x-0 top-2 flex items-center justify-between px-3 text-white">
					<span className="text-sm font-bold">Community Clips</span>
					<Search className="size-[18px]" />
				</div>
				<div className="absolute bottom-[88px] right-2 flex flex-col items-center gap-3 text-white">
					<span className="flex flex-col items-center gap-0.5 text-[9px]">
						<CirclePlus className="size-6" />
						Create
					</span>
					<span className="flex flex-col items-center gap-0.5 text-[9px]">
						<Heart className="size-7" fill={MOBILE_TOKENS.error} style={{ color: MOBILE_TOKENS.error }} />
						{video.likes}
					</span>
					<span className="flex flex-col items-center gap-0.5 text-[9px]">
						<MessageCircle className="size-6" fill="currentColor" />
						{video.comments}
					</span>
					<span className="flex flex-col items-center gap-0.5 text-[9px]">
						<Share2 className="size-6" fill="currentColor" />
						Share
					</span>
				</div>
				<div className="absolute bottom-8 left-3 right-14 flex flex-col gap-1.5 text-white">
					<span className="flex items-center gap-2">
						<MockAvatar person={author} size={28} />
						<span className="min-w-0">
							<span className="block truncate text-[12px] font-bold">{author.name}</span>
							<span className="block truncate text-[10px] text-white/50">{video.groups.join(", ")}</span>
						</span>
					</span>
					<p className="line-clamp-3 text-[11px] leading-[15px]">{video.caption}</p>
				</div>
				<Volume2 className="absolute bottom-3 right-3 size-5 text-white" />
			</div>
		</MobileAppShell>
	);
}
